import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import clsx from 'clsx';
import { Spinner } from '@oysterjs/ui/spinner';
import { getStaticsBaseUrl } from '@oysterjs/core/config';
import { SanitizedHtml } from '@oysterjs/ais/components/sanitized-html';

/**
 * The statics routes a citation can point at, as the preview endpoint names them.
 *
 * `doc` is an agency file, cited by SEARCH_FILE and PEEK_FILE with a quote and
 * usually the chunk it came from. `kb` is a knowledge base document, cited by
 * SEARCH_KNOWLEDGE_BASE with a page number and no quote — those previews show
 * the cited page rather than a highlighted passage.
 */
export type CitationKind = 'doc' | 'kb';

/** Fields a resolved citation marker carries on its `href`. */
export type CitationRef = {
  href: string;
  /** Last path segment, e.g. `file_2xKmQ9wTfBhJ4Rny` or `kbdoc_5QwhgZ27BJrbgGcT`. */
  id: string;
  kind: CitationKind;
  quote: string | null;
  /**
   * The locator from the fragment: a zero-based chunk number for agency files,
   * a one-based page number for knowledge base documents. Null when the citation
   * named neither.
   */
  chunk: number | null;
};

export type CitationPreview = {
  href: string;
  title: string;
  /** HTML snippet; when matched, the quote is wrapped in `<mark class="citation-quote-highlight">`. */
  excerpt: string;
  /** False when the excerpt is surrounding context rather than the cited passage. */
  matched: boolean;
  /**
   * False when the highlight was located by tolerance rather than matched
   * verbatim, so the marked words may not be the ones cited. Only meaningful
   * alongside `matched`.
   */
  exact: boolean;
};

/**
 * `/agency/files/` is a path an earlier iteration emitted. The footnote plugin
 * rewrites it to `/agency/doc/` before a marker renders, but mapping it here too
 * means a citation that reached the DOM another way still resolves.
 */
const CITATION_KIND_BY_ROUTE: Record<string, CitationKind> = {
  doc: 'doc',
  files: 'doc',
  kb: 'kb'
};

const CITATION_ROUTE_RE = /^\/agency\/(doc|files|kb)\//;

/**
 * Read the preview's fetch key off the href the footnote plugin resolved.
 * Returns null for in-page jumps, third-party links, and unparseable URLs —
 * anything with no Cara document behind it to preview.
 */
export const parseCitationHref = (href: string): CitationRef | null => {
  let parsed: URL;
  try {
    parsed = new URL(href);
  } catch {
    return null;
  }

  const kind = CITATION_KIND_BY_ROUTE[parsed.pathname.match(CITATION_ROUTE_RE)?.[1] ?? ''];
  if (!kind) {
    return null;
  }

  const segments = parsed.pathname.split('/').filter(Boolean);
  const id = segments[segments.length - 1];
  if (!id) {
    return null;
  }

  return {
    href,
    id,
    kind,
    quote: parsed.searchParams.get('quote'),
    chunk: parseCitationChunk(parsed.hash)
  };
};

/**
 * Read the locator out of a citation's URL fragment. Agency files carry a bare
 * `#4` and knowledge base documents a `#page=3`; both forms are in circulation,
 * and dropping either loses the page the citation pointed at.
 */
const parseCitationChunk = (hash: string): number | null => {
  const raw = hash.replace(/^#/, '');
  if (!raw) {
    return null;
  }
  const value = raw.includes('=') ? raw.split('=')[1] : raw;
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
};

export const citationPreviewUrl = (ref: CitationRef): string => {
  const params = new URLSearchParams({ kind: ref.kind, id: ref.id });
  if (ref.quote) {
    params.set('quote', ref.quote);
  }
  if (ref.chunk !== null) {
    params.set('chunk', String(ref.chunk));
  }
  return `${getStaticsBaseUrl()}/agency/citation-preview?${params.toString()}`;
};

/**
 * Previews keyed by request URL. A citation's snippet only changes when its
 * document is re-indexed, so re-hovering a marker — which readers do constantly
 * while comparing citations — should not re-request it.
 */
const previewCache = new Map<string, Promise<CitationPreview>>();

export const fetchCitationPreview = (
  ref: CitationRef,
  signal?: AbortSignal
): Promise<CitationPreview> => {
  const url = citationPreviewUrl(ref);

  const cached = previewCache.get(url);
  if (cached) {
    return cached;
  }

  const request = fetch(url, { signal })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`citation preview failed: ${response.status}`);
      }
      return response.json();
    })
    .then((data) => ({
      href: data.href ?? ref.href,
      title: data.title ?? '',
      excerpt: data.snippet ?? '',
      matched: data.matched === true,
      // Only an explicit false is inexact. A response that omits the field
      // has no tolerant match to report.
      exact: data.exact !== false
    }))
    .catch((error) => {
      // A failed request must not stay cached, or one dropped connection
      // poisons that marker for the rest of the session.
      previewCache.delete(url);
      throw error;
    });

  previewCache.set(url, request);
  return request;
};

/** Exposed for tests; production code has no reason to drop the cache. */
export const clearCitationPreviewCache = () => previewCache.clear();

const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 120;

/** Gap between the marker and the card, and between the card and the viewport edge. */
const CARD_OFFSET_PX = 6;
const VIEWPORT_MARGIN_PX = 8;

/** Kept in step with the card's `max-w-[580px]` and `max-h-96` classes. */
const CARD_MAX_WIDTH_PX = 580;
const CARD_MAX_HEIGHT_PX = 384;

/** True when react-markdown forwarded the GFM footnote-ref flag onto the anchor. */
export const isFootnoteRefAnchor = (props: Record<string, unknown>): boolean =>
  'data-footnote-ref' in props || 'dataFootnoteRef' in props;

type MarkdownAnchorProps = React.ComponentPropsWithoutRef<'a'> & {
  node?: unknown;
};

type CardPosition = { top: number; left: number; placement: 'below' | 'above' };

/**
 * Place the card under its marker, flipping above when it would overhang the
 * bottom and pulling it left when it would overhang the right. Without the flip,
 * a citation low in a long answer opens a card that is mostly off screen — and
 * the citations at the end of an answer are exactly the ones readers check.
 */
export const positionCitationCard = (rect: DOMRect): CardPosition => {
  const spaceBelow = window.innerHeight - rect.bottom - CARD_OFFSET_PX;
  const spaceAbove = rect.top - CARD_OFFSET_PX;
  const above = spaceBelow < Math.min(CARD_MAX_HEIGHT_PX, spaceAbove);

  const maxLeft = window.innerWidth - CARD_MAX_WIDTH_PX - VIEWPORT_MARGIN_PX;
  return {
    top: above ? rect.top - CARD_OFFSET_PX : rect.bottom + CARD_OFFSET_PX,
    left: Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft)),
    placement: above ? 'above' : 'below'
  };
};

type PreviewState = 'loading' | 'ready' | 'error';

/**
 * react-markdown `components.a` override: on hover or focus, shows a portaled
 * preview of the cited passage. Keeps `sup > a` so citation CSS still matches,
 * and leaves non-citation links entirely alone.
 */
export const CitationMarkdownAnchor = (props: MarkdownAnchorProps) => {
  const { node: _node, href, children, ...rest } = props;
  const isCitationMarker = isFootnoteRefAnchor(rest as Record<string, unknown>);
  // Stable on href so opening the card does not re-derive on every parent render.
  const citation = React.useMemo(
    () => (typeof href === 'string' && isCitationMarker ? parseCitationHref(href) : null),
    [href, isCitationMarker]
  );

  const cardId = React.useId();
  const triggerRef = React.useRef<HTMLAnchorElement | null>(null);
  const [open, setOpen] = React.useState(false);
  const [position, setPosition] = React.useState<CardPosition | null>(null);
  const [preview, setPreview] = React.useState<CitationPreview | null>(null);
  const [state, setState] = React.useState<PreviewState>('loading');

  const timers = React.useRef<number[]>([]);
  const clearTimers = React.useCallback(() => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  }, []);
  const later = React.useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);

  const updatePosition = React.useCallback(() => {
    if (triggerRef.current) {
      setPosition(positionCitationCard(triggerRef.current.getBoundingClientRect()));
    }
  }, []);

  const scheduleOpen = React.useCallback(() => {
    if (!citation) {
      return;
    }
    clearTimers();
    later(() => {
      updatePosition();
      setOpen(true);
    }, OPEN_DELAY_MS);
  }, [citation, clearTimers, later, updatePosition]);

  const scheduleClose = React.useCallback(() => {
    if (!citation) {
      return;
    }
    clearTimers();
    later(() => setOpen(false), CLOSE_DELAY_MS);
  }, [citation, clearTimers, later]);

  React.useEffect(() => clearTimers, [clearTimers]);

  React.useEffect(() => {
    if (!open || !citation) {
      return;
    }

    // The preview survives the card closing, so reopening a marker the reader
    // already looked at shows its snippet immediately rather than a spinner.
    const controller = new AbortController();
    setState('loading');
    fetchCitationPreview(citation, controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) {
          setPreview(result);
          setState('ready');
        }
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setState('error');
        }
      });

    return () => controller.abort();
  }, [open, citation]);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    const onScroll = () => updatePosition();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearTimers();
        setOpen(false);
      }
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, updatePosition, clearTimers]);

  const reduceMotion = useReducedMotion();

  const enterTransition = reduceMotion
    ? { duration: 0.12, ease: 'easeOut' as const }
    : { type: 'spring' as const, stiffness: 380, damping: 28, mass: 0.7 };
  const exitTransition = reduceMotion
    ? { duration: 0.1, ease: 'easeIn' as const }
    : { duration: 0.16, ease: [0.4, 0, 1, 1] as const };

  return (
    <>
      <a
        ref={triggerRef}
        href={href}
        aria-describedby={open ? cardId : undefined}
        {...rest}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={scheduleOpen}
        onBlur={scheduleClose}
      >
        {children}
      </a>
      {citation &&
        createPortal(
          <AnimatePresence>
            {open && position && (
              // Keyed motion node must be AnimatePresence's direct child so exit
              // runs; perspective lives on the same element as rotateX.
              <motion.div
                key="citation-hover"
                id={cardId}
                role="tooltip"
                className={clsx(
                  'fixed z-[100] max-h-96 min-w-72 max-w-[580px] overflow-y-auto rounded-lg border p-3 shadow-lg',
                  position.placement === 'above' ? 'origin-bottom' : 'origin-top',
                  'border-neutral-200 bg-white text-neutral-800',
                  'dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-100'
                )}
                style={{
                  top: position.top,
                  left: position.left,
                  transform: position.placement === 'above' ? 'translateY(-100%)' : undefined,
                  transformPerspective: 900,
                  willChange: 'transform, opacity'
                }}
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, rotateX: 58, y: -4 }}
                animate={reduceMotion ? { opacity: 1 } : { opacity: 1, rotateX: 0, y: 0 }}
                exit={
                  reduceMotion
                    ? { opacity: 0, transition: exitTransition }
                    : { opacity: 0, rotateX: 28, y: -2, transition: exitTransition }
                }
                transition={enterTransition}
                onMouseEnter={scheduleOpen}
                onMouseLeave={scheduleClose}
              >
                <CitationCardBody
                  state={state}
                  preview={preview}
                  fallbackHref={citation.href}
                  reduceMotion={!!reduceMotion}
                />
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  );
};

type CitationCardBodyProps = {
  state: PreviewState;
  preview: CitationPreview | null;
  fallbackHref: string;
  reduceMotion: boolean;
};

/**
 * The card always says something. A preview that failed, or that resolved to a
 * document with nothing quotable in it, still gets a message and a way through
 * to the source — an empty bordered box reads as a broken citation.
 */
const CitationCardBody = (props: CitationCardBodyProps) => {
  if (props.state === 'loading') {
    return (
      <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400">
        <Spinner size="size-3.5" color="neutral" />
        Loading citation…
      </div>
    );
  }

  if (props.state === 'error' || !props.preview) {
    return (
      <div className="flex flex-col gap-1.5">
        <div className="text-xs text-neutral-500 dark:text-neutral-400">Preview unavailable.</div>
        <CitationSourceLink href={props.fallbackHref} />
      </div>
    );
  }

  const { preview } = props;
  return (
    <div className="flex flex-col gap-1.5">
      {preview.title && (
        <div className="truncate text-[11px] font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
          {preview.title}
        </div>
      )}
      {preview.excerpt ? (
        <>
          {!preview.matched && (
            <div className="text-[11px] italic text-neutral-500 dark:text-neutral-400">
              Showing the cited page; the exact passage could not be located.
            </div>
          )}
          {preview.matched && !preview.exact && (
            <div className="text-[11px] italic text-neutral-500 dark:text-neutral-400">
              Closest match; the highlight may not be the exact passage cited.
            </div>
          )}
          <SanitizedHtml
            html={preview.excerpt}
            className={clsx(
              'citation-excerpt text-sm leading-snug text-pretty',
              // The wash is translucent, so the alpha is doing the work of a color:
              // over the dark card, anything much below this composites to brown
              // rather than yellow, and much above it stops carrying the light ink.
              '[--citation-hl:theme(colors.amber.200)] dark:[--citation-hl:theme(colors.amber.400/55%)]',
              props.reduceMotion && 'citation-excerpt-reduce-motion'
            )}
          />
        </>
      ) : (
        <div className="text-xs text-neutral-500 dark:text-neutral-400">
          No preview available for this source.
        </div>
      )}
      <CitationSourceLink href={preview.href || props.fallbackHref} />
    </div>
  );
};

const CitationSourceLink = (props: { href: string }) =>
  props.href ? (
    <a
      href={props.href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs font-medium text-sky-600 dark:text-sky-400"
    >
      View source page →
    </a>
  ) : null;
